/**
 * ScheduleDialog Component
 * Dialog for creating and editing workflow schedules and system task schedules
 */

import { useState, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { Calendar, Clock, Repeat, Terminal, X, Loader2, Settings, Workflow } from 'lucide-react';
import { useCreateSchedule, useUpdateSchedule } from '../hooks/useWorkflowSchedules';
import {
  useCreateSystemTaskSchedule,
  useUpdateSystemTaskSchedule,
  useSystemTaskAvailableTypes,
} from '../hooks/useSystemTaskSchedules';
import { useWorkflows } from '@/features/image-workflows/hooks/useImageWorkflows';
import { WorkflowTargetSelector } from '@/components/common/WorkflowTargetSelector';
import { UserFilterOptionsForm } from '@/components/common/UserFilterOptionsForm';
import { RuntimeOptionsForm } from '@/features/image-workflows/components/RuntimeOptionsForm';
import { useAuthStore } from '@/stores/authStore';
import type {
  WorkflowSchedule,
  ScheduleType,
  RecurringInterval,
  ScheduleCreateRequest,
  ScheduleUpdateRequest,
  RuntimeOptions,
} from '@/types/workflowSchedule';
import type { UserFilterOptions } from '@/types/imageWorkflow';
import type {
  SystemTaskSchedule,
  ScheduleCategory,
  SystemTaskScheduleCreateRequest,
  SystemTaskScheduleUpdateRequest,
} from '@/types/systemTaskSchedule';
import type { WorkflowTarget } from '@/types/users';
import { SCHEDULE_TYPE_LABELS, RECURRING_INTERVAL_LABELS } from '@/types/workflowSchedule';
import {
  SCHEDULE_CATEGORY_LABELS,
  SCHEDULE_CATEGORY_DESCRIPTIONS,
  SYSTEM_TASK_TYPE_LABELS,
  SYSTEM_TASK_TYPE_DESCRIPTIONS,
} from '@/types/systemTaskSchedule';

interface ScheduleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schedule?: WorkflowSchedule | SystemTaskSchedule | null;
  scheduleCategory?: ScheduleCategory;
  onSuccess?: () => void;
}

const DAYS_OF_WEEK = [
  { value: 0, label: '周日' },
  { value: 1, label: '周一' },
  { value: 2, label: '周二' },
  { value: 3, label: '周三' },
  { value: 4, label: '周四' },
  { value: 5, label: '周五' },
  { value: 6, label: '周六' },
];

const DEFAULT_DAYS_OF_WEEK = [1, 2, 3, 4, 5]; // Mon-Fri

const TIMEZONE_OPTIONS = [
  { value: 'Asia/Shanghai', label: '亚洲/上海' },
  { value: 'Asia/Tokyo', label: '亚洲/东京' },
  { value: 'America/New_York', label: '美国/纽约' },
  { value: 'Europe/London', label: '欧洲/伦敦' },
  { value: 'UTC', label: 'UTC' },
];

// Helper to determine if a schedule is a workflow schedule
function isWorkflowSchedule(schedule: WorkflowSchedule | SystemTaskSchedule): schedule is WorkflowSchedule {
  return 'workflow_id' in schedule;
}

export function ScheduleDialog({
  open,
  onOpenChange,
  schedule,
  scheduleCategory: initialCategory,
  onSuccess,
}: ScheduleDialogProps) {
  // Determine if editing and category
  const isEditing = !!schedule;
  const detectedCategory: ScheduleCategory = schedule
    ? isWorkflowSchedule(schedule)
      ? 'workflow'
      : 'system_task'
    : initialCategory || 'workflow';

  // Auth store for current user
  const user = useAuthStore((state) => state.user);

  // Category state (locked when editing)
  const [category, setCategory] = useState<ScheduleCategory>(detectedCategory);

  // Workflow list (for workflow schedules)
  const { data: workflowsData } = useWorkflows();
  const workflows = workflowsData?.workflows || [];

  // System task types (for system task schedules)
  const { data: taskTypesData } = useSystemTaskAvailableTypes();
  const availableTaskTypes = taskTypesData?.task_types || [];

  // Mutations - Workflow
  const createWorkflowSchedule = useCreateSchedule();
  const updateWorkflowSchedule = useUpdateSchedule();

  // Mutations - System Task
  const createSystemTaskSchedule = useCreateSystemTaskSchedule();
  const updateSystemTaskSchedule = useUpdateSystemTaskSchedule();

  // Form state - Basic info
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  // Workflow specific
  const [workflowId, setWorkflowId] = useState('');

  // System task specific
  const [taskType, setTaskType] = useState('');
  const [taskConfig, setTaskConfig] = useState('{}');

  // Schedule type and config
  const [scheduleType, setScheduleType] = useState<ScheduleType>('once');

  // Once config
  const [runAt, setRunAt] = useState('');

  // Recurring config
  const [interval, setInterval] = useState<RecurringInterval>('daily');
  const [time, setTime] = useState('10:00');
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>(DEFAULT_DAYS_OF_WEEK);
  const [daysOfMonth, setDaysOfMonth] = useState<number[]>([1, 15]);
  const [timezone, setTimezone] = useState('Asia/Shanghai');

  // Cron config
  const [cronExpression, setCronExpression] = useState('0 10 * * 1-5');

  // Target users (using WorkflowTargetSelector) - only for workflow schedules
  const [target, setTarget] = useState<WorkflowTarget>({
    type: 'current_user',
    user_ids: [],
  });

  // User filter options (only for all_valid_users) - only for workflow schedules
  const [userFilter, setUserFilter] = useState<UserFilterOptions>({
    exclude_test: true,
    has_self_birth_chart: false,
  });

  // Runtime options (default: protect user memory) - only for workflow schedules
  const [runtimeOptions, setRuntimeOptions] = useState<RuntimeOptions>({
    disable_memory_tool: true,
    disable_memory_write: true,
  });

  // Lock body scroll when modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // Initialize form from schedule
  useEffect(() => {
    if (schedule) {
      // Set category based on schedule type
      const cat = isWorkflowSchedule(schedule) ? 'workflow' : 'system_task';
      setCategory(cat);

      setName(schedule.name);
      setDescription(schedule.description || '');
      setScheduleType(schedule.schedule_type);

      const config = schedule.schedule_config;
      if (schedule.schedule_type === 'once' && config.run_at) {
        const date = new Date(config.run_at as string);
        setRunAt(date.toISOString().slice(0, 16));
      } else if (schedule.schedule_type === 'recurring') {
        setInterval((config.interval as RecurringInterval) || 'daily');
        setTime((config.time as string) || '10:00');
        setTimezone((config.timezone as string) || 'Asia/Shanghai');
        if (config.days_of_week) {
          setDaysOfWeek(config.days_of_week as number[]);
        }
        if (config.days_of_month) {
          setDaysOfMonth(config.days_of_month as number[]);
        }
      } else if (schedule.schedule_type === 'cron' && config.expression) {
        setCronExpression(config.expression as string);
        setTimezone((config.timezone as string) || 'Asia/Shanghai');
      }

      if (isWorkflowSchedule(schedule)) {
        setWorkflowId(schedule.workflow_id);
        // Read target_type from schedule, default to 'current_user' for backward compatibility
        const targetType = schedule.target_type || 'current_user';
        const targetUsers = schedule.target_user_ids || [];
        setTarget({ type: targetType, user_ids: targetUsers });
        // Read runtime options from schedule, default to protect user memory
        setRuntimeOptions(schedule.runtime_options || {
          disable_memory_tool: true,
          disable_memory_write: true,
        });
      } else {
        // System task schedule
        setTaskType(schedule.task_type);
        setTaskConfig(JSON.stringify(schedule.task_config || {}, null, 2));
      }
    } else {
      // Reset form for new schedule
      setCategory(initialCategory || 'workflow');
      resetForm();
    }
  }, [schedule, open, initialCategory]);

  const resetForm = () => {
    setName('');
    setDescription('');
    setWorkflowId('');
    setTaskType('');
    setTaskConfig('{}');
    setScheduleType('once');
    setRunAt('');
    setInterval('daily');
    setTime('10:00');
    setDaysOfWeek(DEFAULT_DAYS_OF_WEEK);
    setDaysOfMonth([1, 15]);
    setTimezone('Asia/Shanghai');
    setCronExpression('0 10 * * 1-5');
    setTarget({ type: 'current_user', user_ids: [] });
    setRuntimeOptions({ disable_memory_tool: true, disable_memory_write: true });
  };

  // Build schedule config
  const buildScheduleConfig = (): Record<string, unknown> => {
    if (scheduleType === 'once') {
      return {
        run_at: new Date(runAt).toISOString(),
      };
    }

    if (scheduleType === 'recurring') {
      const config: Record<string, unknown> = {
        interval,
        time,
        timezone,
      };
      if (interval === 'weekly') {
        config.days_of_week = daysOfWeek;
      }
      if (interval === 'monthly') {
        config.days_of_month = daysOfMonth;
      }
      return config;
    }

    if (scheduleType === 'cron') {
      return {
        expression: cronExpression,
        timezone,
      };
    }

    return {};
  };

  // Get target user IDs
  const getTargetUserIds = (): string[] => {
    return target.user_ids;
  };

  // Handle submit
  const handleSubmit = useCallback(async () => {
    if (!name.trim()) {
      return;
    }

    const scheduleConfig = buildScheduleConfig();

    if (category === 'workflow') {
      if (!workflowId) {
        return;
      }

      const targetUserIds = getTargetUserIds();

      if (isEditing && schedule && isWorkflowSchedule(schedule)) {
        const updateData: ScheduleUpdateRequest = {
          name,
          description: description || undefined,
          schedule_type: scheduleType,
          schedule_config: scheduleConfig,
          target_type: target.type,
          target_user_ids: targetUserIds,
          // Only include user_filter when target_type is 'all_valid_users'
          user_filter: target.type === 'all_valid_users' ? userFilter : undefined,
          runtime_options: runtimeOptions,
        };

        try {
          await updateWorkflowSchedule.mutateAsync({ id: schedule.id, data: updateData });
          onOpenChange(false);
          onSuccess?.();
        } catch {
          // Error handled by hook
        }
      } else {
        const createData: ScheduleCreateRequest = {
          workflow_id: workflowId,
          name,
          description: description || undefined,
          schedule_type: scheduleType,
          schedule_config: scheduleConfig,
          target_type: target.type,
          target_user_ids: targetUserIds,
          // Only include user_filter when target_type is 'all_valid_users'
          user_filter: target.type === 'all_valid_users' ? userFilter : undefined,
          runtime_options: runtimeOptions,
        };

        try {
          await createWorkflowSchedule.mutateAsync(createData);
          onOpenChange(false);
          onSuccess?.();
        } catch {
          // Error handled by hook
        }
      }
    } else {
      // System task schedule
      if (!taskType) {
        return;
      }

      let parsedTaskConfig: Record<string, unknown> = {};
      try {
        parsedTaskConfig = JSON.parse(taskConfig);
      } catch {
        // Use empty object if JSON is invalid
      }

      if (isEditing && schedule && !isWorkflowSchedule(schedule)) {
        const updateData: SystemTaskScheduleUpdateRequest = {
          name,
          description: description || undefined,
          schedule_type: scheduleType,
          schedule_config: scheduleConfig,
          task_config: parsedTaskConfig,
        };

        try {
          await updateSystemTaskSchedule.mutateAsync({ id: schedule.id, data: updateData });
          onOpenChange(false);
          onSuccess?.();
        } catch {
          // Error handled by hook
        }
      } else {
        const createData: SystemTaskScheduleCreateRequest = {
          task_type: taskType,
          name,
          description: description || undefined,
          schedule_type: scheduleType,
          schedule_config: scheduleConfig,
          task_config: parsedTaskConfig,
        };

        try {
          await createSystemTaskSchedule.mutateAsync(createData);
          onOpenChange(false);
          onSuccess?.();
        } catch {
          // Error handled by hook
        }
      }
    }
  }, [
    name,
    description,
    category,
    workflowId,
    taskType,
    taskConfig,
    scheduleType,
    isEditing,
    schedule,
    target,
    runtimeOptions,
    createWorkflowSchedule,
    updateWorkflowSchedule,
    createSystemTaskSchedule,
    updateSystemTaskSchedule,
    onOpenChange,
    onSuccess,
  ]);

  const toggleDayOfWeek = (day: number) => {
    setDaysOfWeek((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort()
    );
  };

  const isSubmitting =
    createWorkflowSchedule.isPending ||
    updateWorkflowSchedule.isPending ||
    createSystemTaskSchedule.isPending ||
    updateSystemTaskSchedule.isPending;

  // Validate schedule config based on type
  const isScheduleConfigValid = (): boolean => {
    if (scheduleType === 'once') {
      return !!runAt && !isNaN(new Date(runAt).getTime());
    }
    if (scheduleType === 'cron') {
      return !!cronExpression.trim();
    }
    return true;
  };

  // Validate form based on category
  const isFormValid = (): boolean => {
    if (!name.trim()) return false;
    if (!isScheduleConfigValid()) return false;

    if (category === 'workflow') {
      return !!workflowId;
    } else {
      return !!taskType;
    }
  };

  // Workflow options
  const workflowOptions = [
    { value: '', label: '选择要执行的工作流' },
    ...workflows.map((w) => ({ value: w.id, label: w.name })),
  ];

  // Task type options (from API or fallback to static)
  const taskTypeOptions = [
    { value: '', label: '选择任务类型' },
    ...(availableTaskTypes.length > 0
      ? availableTaskTypes.map((t) => ({ value: t.type, label: t.name }))
      : Object.entries(SYSTEM_TASK_TYPE_LABELS).map(([value, label]) => ({ value, label }))),
  ];

  // Interval options
  const intervalOptions = Object.entries(RECURRING_INTERVAL_LABELS).map(([value, label]) => ({
    value,
    label,
  }));

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={() => onOpenChange(false)} />

      {/* Dialog */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">
              {isEditing ? '编辑定时任务' : '创建定时任务'}
            </h2>
          </div>
          <button onClick={() => onOpenChange(false)} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto flex-1 space-y-6">
          {/* Category Selector (only for new schedules) */}
          {!isEditing && (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">任务类别 *</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setCategory('workflow')}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    category === 'workflow'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Workflow
                      className={`h-5 w-5 ${category === 'workflow' ? 'text-blue-600' : 'text-gray-400'}`}
                    />
                    <span
                      className={`font-medium ${category === 'workflow' ? 'text-blue-900' : 'text-gray-900'}`}
                    >
                      {SCHEDULE_CATEGORY_LABELS.workflow}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">{SCHEDULE_CATEGORY_DESCRIPTIONS.workflow}</p>
                </button>
                <button
                  type="button"
                  onClick={() => setCategory('system_task')}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    category === 'system_task'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Settings
                      className={`h-5 w-5 ${category === 'system_task' ? 'text-blue-600' : 'text-gray-400'}`}
                    />
                    <span
                      className={`font-medium ${category === 'system_task' ? 'text-blue-900' : 'text-gray-900'}`}
                    >
                      {SCHEDULE_CATEGORY_LABELS.system_task}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">{SCHEDULE_CATEGORY_DESCRIPTIONS.system_task}</p>
                </button>
              </div>
            </div>
          )}

          {/* Category-specific primary selection */}
          {category === 'workflow' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">工作流 *</label>
              <Select
                value={workflowId}
                onChange={(e) => setWorkflowId(e.target.value)}
                options={workflowOptions}
                disabled={isEditing}
              />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">任务类型 *</label>
              <Select
                value={taskType}
                onChange={(e) => setTaskType(e.target.value)}
                options={taskTypeOptions}
                disabled={isEditing}
              />
              {taskType && SYSTEM_TASK_TYPE_DESCRIPTIONS[taskType as keyof typeof SYSTEM_TASK_TYPE_DESCRIPTIONS] && (
                <p className="text-xs text-gray-500 mt-1">
                  {SYSTEM_TASK_TYPE_DESCRIPTIONS[taskType as keyof typeof SYSTEM_TASK_TYPE_DESCRIPTIONS]}
                </p>
              )}
            </div>
          )}

          {/* Basic Info */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">任务名称 *</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={
                  category === 'workflow' ? '例如：每日用户头像生成' : '例如：每日产物清理'
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">描述</label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="可选的任务描述..."
                rows={2}
              />
            </div>

            {/* System task config (JSON) */}
            {category === 'system_task' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  任务配置 (JSON)
                </label>
                <Textarea
                  value={taskConfig}
                  onChange={(e) => setTaskConfig(e.target.value)}
                  placeholder='{"delete_files": true, "dry_run": false}'
                  rows={3}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  可选的任务配置参数，格式为 JSON
                </p>
              </div>
            )}
          </div>

          {/* Schedule Type Tabs */}
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">执行类型 *</label>
            <Tabs
              value={scheduleType}
              onValueChange={(value) => setScheduleType(value as ScheduleType)}
            >
              <TabsList className="w-full">
                <TabsTrigger value="once" className="flex-1 flex items-center justify-center gap-2">
                  <Clock className="h-4 w-4" />
                  {SCHEDULE_TYPE_LABELS.once}
                </TabsTrigger>
                <TabsTrigger
                  value="recurring"
                  className="flex-1 flex items-center justify-center gap-2"
                >
                  <Repeat className="h-4 w-4" />
                  {SCHEDULE_TYPE_LABELS.recurring}
                </TabsTrigger>
                <TabsTrigger value="cron" className="flex-1 flex items-center justify-center gap-2">
                  <Terminal className="h-4 w-4" />
                  {SCHEDULE_TYPE_LABELS.cron}
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Once Config */}
            {scheduleType === 'once' && (
              <div className="p-4 border rounded-lg bg-gray-50 space-y-2">
                <label className="block text-sm font-medium text-gray-700">执行时间</label>
                <Input
                  type="datetime-local"
                  value={runAt}
                  onChange={(e) => setRunAt(e.target.value)}
                />
                <p className="text-xs text-gray-500">任务将在指定时间执行一次后标记为已完成</p>
              </div>
            )}

            {/* Recurring Config */}
            {scheduleType === 'recurring' && (
              <div className="p-4 border rounded-lg bg-gray-50 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">频率</label>
                    <Select
                      value={interval}
                      onChange={(e) => setInterval(e.target.value as RecurringInterval)}
                      options={intervalOptions}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">执行时间</label>
                    <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
                  </div>
                </div>

                {/* Days of week for weekly */}
                {interval === 'weekly' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      选择执行日
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {DAYS_OF_WEEK.map((day) => (
                        <button
                          key={day.value}
                          type="button"
                          onClick={() => toggleDayOfWeek(day.value)}
                          className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                            daysOfWeek.includes(day.value)
                              ? 'bg-blue-600 text-white'
                              : 'bg-white border text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {day.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Days of month for monthly */}
                {interval === 'monthly' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      选择执行日 (每月)
                    </label>
                    <Input
                      value={daysOfMonth.join(', ')}
                      onChange={(e) => {
                        const days = e.target.value
                          .split(',')
                          .map((s) => parseInt(s.trim(), 10))
                          .filter((n) => !isNaN(n) && n >= 1 && n <= 31);
                        setDaysOfMonth(days);
                      }}
                      placeholder="例如: 1, 15"
                    />
                    <p className="text-xs text-gray-500 mt-1">输入日期，用逗号分隔（1-31）</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">时区</label>
                  <Select
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    options={TIMEZONE_OPTIONS}
                  />
                </div>
              </div>
            )}

            {/* Cron Config */}
            {scheduleType === 'cron' && (
              <div className="p-4 border rounded-lg bg-gray-50 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cron 表达式</label>
                  <Input
                    value={cronExpression}
                    onChange={(e) => setCronExpression(e.target.value)}
                    placeholder="0 10 * * 1-5"
                    className="font-mono"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    格式: 分 时 日 月 星期 (例如: 0 10 * * 1-5 = 每周一到周五 10:00)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">时区</label>
                  <Select
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    options={TIMEZONE_OPTIONS}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Target Users (only for workflow schedules) */}
          {category === 'workflow' && (
            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">目标用户</label>
              <WorkflowTargetSelector
                value={target}
                onChange={setTarget}
                currentUserId={user?.user_id}
              />
            </div>
          )}

          {/* User Filter Options (only for workflow schedules with all_valid_users) */}
          {category === 'workflow' && target.type === 'all_valid_users' && (
            <UserFilterOptionsForm
              value={userFilter}
              onChange={setUserFilter}
            />
          )}

          {/* Runtime Options (only for workflow schedules) */}
          {category === 'workflow' && (
            <RuntimeOptionsForm
              value={runtimeOptions}
              onChange={setRuntimeOptions}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200">
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting || !isFormValid()}>
            {isSubmitting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            {isEditing ? '保存' : '创建'}
          </Button>
        </div>
      </div>
    </div>
  );
}
